import { useLayoutEffect, useRef, useState } from "react";
import { Stack, Tooltip, Typography, tooltipClasses } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { KeyRoles } from "../../../../types/enums";
import { keyRolesItems } from "../../../../types/genereal";
import { PersonaBubbleChartItem } from "../../../../types";
import { PersonaLegendBlock } from "./PersonaLegendBlock";
import {
  StatsTooltip,
  PersonaStatsTooltipContent,
} from "./PersonaStatsTooltip";
import {
  BAR_HEIGHT,
  ITEM_GAP,
  GROUP_GAP,
  LABEL_COLUMN_WIDTH,
  ROLE_GAP,
  TRACK_OFFSET,
  ITEM_HEIGHT,
  CONTENT_BOTTOM_PADDING,
  MIN_HOVER_PCT,
  TRACK_BG,
  FILL_COLOR_ACTIVE,
  DASH_COLOR,
  hatchBackground,
  getNiceTicks,
  buildPopperModifiers,
} from "./personaGapChart.shared";

const RoleTooltip = styled(
  ({ className, ...props }: React.ComponentProps<typeof Tooltip>) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ),
)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    display: "flex",
    maxWidth: "200px",
    padding: "4px 8px",
    alignItems: "center",
    borderRadius: "4px",
    fontSize: "13px",
    fontWeight: 500,
  },
}));

type Props = {
  personas: PersonaBubbleChartItem[];
  boundaryEl?: HTMLElement | null;
};

export const PersonaGapChart = ({ personas }: Props) => {
  const theme = useTheme();
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const [openRoleKey, setOpenRoleKey] = useState<string | null>(null);
  const [canScroll, setCanScroll] = useState(false);
  const [hoveredPersonaId, setHoveredPersonaId] = useState<string | null>(null);

  const fillColorInactive = theme.palette.custom.brandBlueBorder;
  const personaNameColor = theme.palette.custom.brandBlue;
  const expectedMarkerColor = theme.palette.primary.dark;
  const zeroTickColor = theme.palette.custom.textDisabled;
  const gridTickColor = theme.palette.custom.surfaceDisabled;

  const groupOrder: string[] = [];
  const groups: Record<string, PersonaBubbleChartItem[]> = {};
  personas.forEach((p) => {
    if (!groups[p.role]) {
      groups[p.role] = [];
      groupOrder.push(p.role);
    }
    groups[p.role].push(p);
  });

  const groupHeights = groupOrder.map((role) => {
    const items = groups[role];
    return items.length * ITEM_HEIGHT + (items.length - 1) * ITEM_GAP;
  });
  const contentHeight =
    groupHeights.reduce((sum, h) => sum + h, 0) +
    Math.max(groupOrder.length - 1, 0) * GROUP_GAP +
    CONTENT_BOTTOM_PADDING;

  useLayoutEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setCanScroll(contentHeight - el.clientHeight > 1);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [contentHeight]);

  if (!personas.length) {
    return (
      <Stack padding="24px" alignItems="center">
        <Typography color="text.secondary">
          No persona data available.
        </Typography>
      </Stack>
    );
  }

  const maxValue = Math.max(
    1,
    ...personas.map((p) => Math.max(p.actual, p.expected)),
  );
  const ticks = getNiceTicks(maxValue);
  const axisMax = ticks[ticks.length - 1] || 1;
  const toPercent = (value: number) => (value / axisMax) * 100;

  const popperModifiers = buildPopperModifiers(scrollAreaRef.current);

  return (
    <Stack
      flexDirection="row"
      gap="16px"
      flexGrow={1}
      minHeight={0}
      sx={{ height: "100%", overflow: "hidden" }}
    >
      <Stack flexGrow={1} minWidth={0} minHeight={0} sx={{ height: "100%" }}>
        <Stack
          ref={scrollAreaRef}
          onScroll={() => setOpenRoleKey(null)}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: canScroll ? "auto" : "hidden",
            overflowX: "hidden",
            scrollbarGutter: "stable",
          }}
        >
          <Stack
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: canScroll ? "flex-start" : "flex-end",
              minHeight: canScroll ? "auto" : "100%",
            }}
          >
            <Stack
              sx={{
                position: "absolute",
                left: `${TRACK_OFFSET}px`,
                right: 0,
                top: 0,
                bottom: 0,
                zIndex: 0,
                pointerEvents: "none",
              }}
            >
              {ticks.map((t) => (
                <Stack
                  key={t}
                  sx={{
                    position: "absolute",
                    left: `${toPercent(t)}%`,
                    top: 0,
                    bottom: 0,
                    width: "1px",
                    backgroundColor: t === 0 ? zeroTickColor : gridTickColor,
                  }}
                />
              ))}
            </Stack>

            <Stack
              gap={`${GROUP_GAP}px`}
              sx={{
                position: "relative",
                zIndex: 1,
                paddingBottom: `${CONTENT_BOTTOM_PADDING}px`,
              }}
            >
              {groupOrder.map((role) => {
                const items = groups[role];
                const roleMeta = keyRolesItems[role as KeyRoles];
                const groupHeight =
                  items.length * ITEM_HEIGHT + (items.length - 1) * ITEM_GAP;

                return (
                  <Stack key={role} flexDirection="row" gap={`${ROLE_GAP}px`}>
                    <Stack
                      sx={{
                        width: `${LABEL_COLUMN_WIDTH}px`,
                        height: `${groupHeight}px`,
                        position: "relative",
                        flexShrink: 0,
                      }}
                    >
                      <RoleTooltip
                        title={roleMeta.title}
                        placement="top"
                        arrow
                        open={openRoleKey === role}
                        onOpen={() => setOpenRoleKey(role)}
                        onClose={() => setOpenRoleKey(null)}
                        slotProps={{
                          popper: {
                            modifiers: popperModifiers,
                          },
                        }}
                      >
                        <Stack
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: `${groupHeight}px`,
                            transform: "translate(-50%, -50%) rotate(-90deg)",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "4px 8px",
                            borderRadius: "7px",
                            backgroundColor: roleMeta.backgroundColor,
                            cursor: "default",
                          }}
                        >
                          <Typography
                            noWrap
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: roleMeta.color,
                            }}
                          >
                            {roleMeta.title}
                          </Typography>
                        </Stack>
                      </RoleTooltip>
                    </Stack>

                    <Stack
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: `${ITEM_GAP}px`,
                        flex: "1 1 0",
                        minWidth: 0,
                      }}
                    >
                      {items.map((p) => {
                        const actualPct = toPercent(p.actual);
                        const expectedPct = toPercent(p.expected);
                        const isOver = p.actual > p.expected;
                        const fillPct = isOver
                          ? expectedPct
                          : Math.min(actualPct, expectedPct);
                        const barExtentPct = Math.max(
                          actualPct,
                          expectedPct,
                          MIN_HOVER_PCT,
                        );

                        const isActive =
                          hoveredPersonaId === null ||
                          hoveredPersonaId === p.persona_id;
                        const fillColor = isActive
                          ? FILL_COLOR_ACTIVE
                          : fillColorInactive;

                        return (
                          <Stack key={p.persona_id} gap="0px">
                            <Stack
                              sx={{
                                display: "flex",
                                paddingLeft: "8px",
                                justifyContent: "flex-start",
                                alignItems: "left",
                                marginBottom: `${ITEM_GAP}px`,
                              }}
                            >
                              <Typography
                                noWrap
                                title={p.persona_name}
                                sx={{
                                  color: personaNameColor,
                                  fontFeatureSettings: "'liga' off, 'clig' off",
                                  fontFamily: "Inter",
                                  fontSize: "12px",
                                  fontStyle: "normal",
                                  fontWeight: 500,
                                  lineHeight: "16px",
                                  letterSpacing: "0.15px",
                                  textTransform: "capitalize",
                                  textAlign: "left",
                                }}
                              >
                                {p.persona_name}
                              </Typography>
                            </Stack>

                            <Stack
                              sx={{
                                position: "relative",
                                width: "100%",
                                height: `${BAR_HEIGHT}px`,
                                boxSizing: "border-box",
                              }}
                            >
                              {fillPct > 0 && (
                                <Stack
                                  sx={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    height: "100%",
                                    width: `${fillPct}%`,
                                    backgroundColor: fillColor,
                                    transition: "background-color 0.15s ease",
                                  }}
                                />
                              )}

                              {isOver && (
                                <Stack
                                  sx={{
                                    position: "absolute",
                                    left: `${expectedPct}%`,
                                    top: 0,
                                    height: "100%",
                                    width: `${actualPct - expectedPct}%`,
                                    backgroundColor: TRACK_BG,
                                    backgroundImage: hatchBackground(fillColor),
                                    border: `1px solid ${fillColor}`,
                                    boxSizing: "border-box",
                                    transition:
                                      "background-image 0.15s ease, border-color 0.15s ease",
                                  }}
                                />
                              )}

                              {!isOver && p.expected > p.actual && (
                                <Stack
                                  sx={{
                                    position: "absolute",
                                    left: `${actualPct}%`,
                                    top: 0,
                                    height: "100%",
                                    width: `${Math.max(expectedPct - actualPct, 0)}%`,
                                    borderTop: `1px dashed ${DASH_COLOR}`,
                                    borderBottom: `1px dashed ${DASH_COLOR}`,
                                  }}
                                />
                              )}

                              <Stack
                                sx={{
                                  position: "absolute",
                                  left: `calc(${expectedPct}% - 1px)`,
                                  top: 0,
                                  height: "100%",
                                  width: "2px",
                                  backgroundColor: expectedMarkerColor,
                                }}
                              />

                              <StatsTooltip
                                title={
                                  <PersonaStatsTooltipContent
                                    persona={p}
                                    role={role as KeyRoles}
                                  />
                                }
                                placement="top"
                                arrow
                                slotProps={{
                                  popper: {
                                    modifiers: popperModifiers,
                                  },
                                }}
                              >
                                <Stack
                                  onMouseEnter={() =>
                                    setHoveredPersonaId(p.persona_id)
                                  }
                                  onMouseLeave={() =>
                                    setHoveredPersonaId((current) =>
                                      current === p.persona_id ? null : current,
                                    )
                                  }
                                  sx={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    height: "100%",
                                    width: `${barExtentPct}%`,
                                    cursor: "pointer",
                                  }}
                                />
                              </StatsTooltip>
                            </Stack>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        </Stack>

        <Stack flexDirection="row" gap={`${ROLE_GAP}px`} sx={{ flexShrink: 0 }}>
          <Stack sx={{ width: `${LABEL_COLUMN_WIDTH}px`, flexShrink: 0 }} />
          <Stack sx={{ flex: "1 1 0", minWidth: 0 }}>
            <Stack
              sx={{
                width: "100%",
                height: "1px",
                backgroundColor: "#DEDEDE",
                marginTop: "8px",
              }}
            />
            <Stack sx={{ position: "relative", width: "100%", height: "20px" }}>
              {ticks.map((t) => {
                const isLast = t === axisMax;
                return (
                  <Typography
                    key={t}
                    sx={
                      isLast
                        ? {
                            position: "absolute",
                            right: 0,
                            top: "4px",
                            fontSize: "11px",
                            color: "#121212",
                          }
                        : {
                            position: "absolute",
                            left: `${toPercent(t)}%`,
                            top: "4px",
                            transform: "translateX(-50%)",
                            fontSize: "11px",
                            color: "#121212",
                          }
                    }
                  >
                    {t}
                  </Typography>
                );
              })}
            </Stack>
          </Stack>
        </Stack>

        <Typography
          sx={{
            color: "#121212",
            textAlign: "center",
            fontFamily: "Inter",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "22px",
            letterSpacing: "0.3px",
            textTransform: "capitalize",
            marginTop: "12px",
            flexShrink: 0,
          }}
        >
          Identified Contacts Per Role
        </Typography>
      </Stack>

      <PersonaLegendBlock personas={personas} />
    </Stack>
  );
};
